import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { WorkItemListItem, WorkPhase } from '@vwt/contracts';
import { isClosedPhase } from '@vwt/domain';
import { Button, cn, Popover, PopoverContent, PopoverTrigger, Spinner } from '@vwt/ui';
import { api, newIdempotencyKey, POLL_ACTIVE, queryClient } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { currentLocale, useTranslation } from '../../lib/i18n.js';
import { todayInDisplayZone } from '../../lib/format.js';
import { calendarGrid, shiftMonth } from './calendar-grid.js';

const PHASE_COLORS: Record<WorkPhase, string> = {
  TODO: 'border-l-fg-subtle',
  IN_PROGRESS: 'border-l-accent',
  IN_REVIEW: 'border-l-warning',
  INTEGRATED: 'border-l-info',
  DONE: 'border-l-success',
  CANCELED: 'border-l-border-strong',
  EXPIRED: 'border-l-border-strong',
};
const SHOWN_PER_DAY = 4;
const MAX_PAGES = 5;

/**
 * Month calendar of work by due date (ADR-0025). It uses the same filters as the list; dragging an
 * item to another day changes its due date through the normal update (and its checks).
 */
export function WorkCalendar({ filters, month, onMonth, onOpen }: { filters: Record<string, string>; month: string | null; onMonth: (month: string | null) => void; onOpen: (key: string) => void }) {
  const { t } = useTranslation();
  const today = todayInDisplayZone();
  const current = month && /^\d{4}-\d{2}$/.test(month) ? month : today.slice(0, 7);
  const grid = useMemo(() => calendarGrid(current), [current]);
  const { sort: _sort, dir: _dir, due: _due, ...rest } = filters;
  const query = useInfiniteQuery({
    queryKey: ['listWorkItems', 'calendar', rest, grid.from, grid.to],
    queryFn: ({ pageParam, signal }) => api.call('listWorkItems', { query: { ...rest, dueFrom: grid.from, dueTo: grid.to, sort: 'dueDate', limit: '100', ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.meta.nextCursor ?? undefined,
    refetchInterval: POLL_ACTIVE,
  });
  // A month rarely has more than a few hundred dated items; load them all, up to a bound.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const pages = query.data?.pages.length ?? 0;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pages < MAX_PAGES) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, pages, fetchNextPage]);
  const items = useMemo(() => query.data?.pages.flatMap((p) => p?.data ?? []) ?? [], [query.data]);
  const total = query.data?.pages[0]?.meta.total;
  const byDay = useMemo(() => {
    const map = new Map<string, WorkItemListItem[]>();
    for (const item of items) if (item.dueDate) map.set(item.dueDate, [...(map.get(item.dueDate) ?? []), item]);
    return map;
  }, [items]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = async (event: DragEndEvent) => {
    const day = event.over ? String(event.over.id) : null;
    const item = items.find((i) => i.id === event.active.id);
    if (!day || !item || item.dueDate === day) return;
    try {
      await api.call('updateWorkItem', { params: { id: item.id }, idempotencyKey: newIdempotencyKey(), body: { expectedVersion: item.version, dueDate: day } });
    } catch (error) {
      toastError(error);
    }
    await queryClient.invalidateQueries({ queryKey: ['listWorkItems'] });
  };
  const title = new Intl.DateTimeFormat(currentLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${current}-01T00:00:00Z`));
  const weekdays = grid.days.slice(0, 7).map((d) => new Intl.DateTimeFormat(currentLocale(), { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`)));
  return (
    <div className="flex h-full flex-col p-4" data-testid="work-calendar">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="iconSm" variant="ghost" onClick={() => onMonth(shiftMonth(current, -1))} aria-label={t('calendar.previous')}>
          <ChevronLeft />
        </Button>
        <h2 className="min-w-40 text-center text-sm font-semibold first-letter:uppercase">{title}</h2>
        <Button size="iconSm" variant="ghost" onClick={() => onMonth(shiftMonth(current, 1))} aria-label={t('calendar.next')}>
          <ChevronRight />
        </Button>
        <Button size="sm" variant="secondary" onClick={() => onMonth(null)} disabled={current === today.slice(0, 7)}>
          {t('calendar.today')}
        </Button>
        {query.isFetching ? <Spinner className="size-4" /> : null}
        <span className="ml-auto text-xs text-fg-muted">
          {total !== undefined ? t('calendar.count', { count: total }) : null} · {t('calendar.hint')}
        </span>
      </div>
      <DndContext sensors={sensors} onDragEnd={(e) => void onDragEnd(e)}>
        <div className="hidden min-h-0 flex-1 grid-cols-7 grid-rows-[auto] overflow-auto rounded-[var(--radius-lg)] border border-border md:grid" role="grid" aria-label={title}>
          {weekdays.map((w, i) => (
            <div key={i} role="columnheader" className="border-b border-border bg-surface-2 px-2 py-1 text-xs font-medium text-fg-subtle capitalize">
              {w}
            </div>
          ))}
          {grid.days.map((day) => (
            <DayCell key={day} day={day} inMonth={day.startsWith(current)} today={day === today} items={byDay.get(day) ?? []} onOpen={onOpen} />
          ))}
        </div>
      </DndContext>
      <ol className="flex flex-col gap-3 md:hidden">
        {grid.days
          .filter((d) => d.startsWith(current) && byDay.get(d)?.length)
          .map((day) => (
            <li key={day}>
              <h3 className={cn('mb-1 text-xs font-semibold text-fg-muted', day === today && 'text-accent')}>{new Intl.DateTimeFormat(currentLocale(), { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`))}</h3>
              <ul className="flex flex-col gap-1">
                {(byDay.get(day) ?? []).map((item) => (
                  <li key={item.id}>
                    <ItemChip item={item} onOpen={onOpen} draggable={false} />
                  </li>
                ))}
              </ul>
            </li>
          ))}
      </ol>
    </div>
  );
}

function DayCell({ day, inMonth, today, items, onOpen }: { day: string; inMonth: boolean; today: boolean; items: WorkItemListItem[]; onOpen: (key: string) => void }) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: day });
  const extra = items.length - SHOWN_PER_DAY;
  return (
    <div ref={setNodeRef} role="gridcell" aria-label={day} className={cn('flex min-h-28 min-w-0 flex-col gap-1 border-r border-b border-border p-1 [&:nth-child(7n)]:border-r-0', inMonth ? 'bg-surface' : 'bg-surface-2/60', isOver && 'bg-accent-soft/40')}>
      <span className={cn('self-end rounded-full px-1.5 text-xs tabular-nums', today ? 'bg-accent font-semibold text-accent-fg' : inMonth ? 'text-fg-muted' : 'text-fg-subtle')}>{Number(day.slice(8))}</span>
      {items.slice(0, SHOWN_PER_DAY).map((item) => (
        <ItemChip key={item.id} item={item} onOpen={onOpen} />
      ))}
      {extra > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="rounded-sm px-1 text-left text-xs text-fg-muted hover:bg-muted">
              {t('calendar.more', { count: extra })}
            </button>
          </PopoverTrigger>
          <PopoverContent className="flex w-64 flex-col gap-1 p-2">
            {items.slice(SHOWN_PER_DAY).map((item) => (
              <ItemChip key={item.id} item={item} onOpen={onOpen} draggable={false} />
            ))}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

function ItemChip({ item, onOpen, draggable = true }: { item: WorkItemListItem; onOpen: (key: string) => void; draggable?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id, disabled: !draggable || !!item.archivedAt });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const finished = isClosedPhase(item.phase);
  return (
    <button
      ref={setNodeRef}
      type="button"
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(item.key)}
      title={`${item.key} · ${item.title}`}
      data-testid="calendar-item"
      className={cn('flex w-full min-w-0 items-center gap-1 rounded-sm border border-l-[3px] border-border bg-surface px-1.5 py-0.5 text-left text-xs shadow-xs hover:border-border-strong', PHASE_COLORS[item.phase], finished && 'text-fg-subtle line-through', isDragging && 'z-50 opacity-80 shadow-lg')}
    >
      <span className="shrink-0 font-mono text-[10px] text-fg-subtle">{item.key}</span>
      <span className="truncate">{item.title}</span>
    </button>
  );
}
